import React, { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  getSortedRowModel,
  getExpandedRowModel,
} from '@tanstack/react-table';
import PropTypes from 'prop-types';
import Pagination from './pagination';

const DataTable = ({
  data = [],
  columns,
  striped,
  hover,
  wrapperClasses,
  classes,
  noDataIndication,
  searchComponent,
  allowPagination = true,
  sizePerPage = 10,
  defaultSorted = [],
  expandRow,
  ...props
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState(defaultSorted);

  const [expanded, setExpanded] = useState({});

  const columnVisibility = useMemo(() => {
    const visibility = {};
    columns.forEach(({ hidden, id } = {}) => {
      if (hidden) {
        visibility[id] = !hidden;
      }
    });

    return visibility;
  }, [columns]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    getRowCanExpand: () => Boolean(expandRow),
    getColumnCanGlobalFilter: column => Boolean(column.columnDef?.searchable),
    initialState: {
      pagination: {
        pageSize: sizePerPage,
      },
    },
    state: {
      sorting,
      globalFilter,
      expanded,
      columnVisibility,
    },
    sortingFns: {
      myDateTime: (a, b, column) => (
        new Date(a.getValue(column)) < new Date(b.getValue(column)) ? -1 : 1
      ),
    },
  });

  const onSearch = val => setGlobalFilter(val);

  return (
    <>
      {searchComponent && searchComponent({ onSearch })}
      <div className="dataTables_wrapper dt-bootstrap4 no-footer">
        <div className={`react-bootstrap-table ${wrapperClasses}`}>
          <table
            className={`table ${striped ? 'table-striped' : ''} ${
              hover ? 'table-hover' : ''
            } ${classes}`}
            {...props}
          >
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {expandRow && (
                    <th
                      className="expand-cell-header"
                      onClick={table.getToggleAllRowsExpandedHandler()}
                    >
                      {expandRow?.expandHeaderColumnRenderer({
                        isAnyExpands: table.getIsSomeRowsExpanded(),
                      })}
                    </th>
                  )}
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      style={header.column.columnDef?.headerStyle}
                      {...{
                        ...header.column.columnDef?.headerAttrs,
                        className:
                          `${header.column.getCanSort() ? 'sortable' : ''} ${
                            header.column.columnDef?.headerAttrs?.className
                            || ''
                          } ${
                            {
                              asc: ' sorting_asc',
                              desc: ' sorting_desc',
                            }[header.column.getIsSorted()] ?? ''
                          }` || {},
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {!header.isPlaceholder && (
                        <>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <>
                  <tr key={row.id}>
                    {expandRow && (
                      <td
                        className="expand-cell"
                        onClick={row.getToggleExpandedHandler()}
                      >
                        {expandRow?.expandColumnRenderer({
                          expanded: row.getIsExpanded(),
                        })}
                      </td>
                    )}
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} {...cell.column.columnDef?.cellProps}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                  {expandRow && row.getIsExpanded() && (
                    <tr>
                      <td
                        className="reset-expansion-style"
                        colSpan={row.getVisibleCells().length + 1}
                      >
                        {expandRow?.renderer(row?.original)}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
            {!table.getRowModel().rows.length && (
              <tr>
                <td colSpan={5}>{noDataIndication}</td>
              </tr>
            )}
          </table>
        </div>
        {!!table.getRowModel().rows.length && allowPagination && (
          <div className="row mb-6">
            <Pagination table={table} sizePerPage={sizePerPage} />
          </div>
        )}
      </div>
    </>
  );
};

DataTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  striped: PropTypes.bool,
  hover: PropTypes.bool,
  wrapperClasses: PropTypes.string,
  classes: PropTypes.string,
  noDataIndication: PropTypes.string,
  searchComponent: PropTypes.func,
  allowPagination: PropTypes.bool,
  sizePerPage: PropTypes.number,
  defaultSorted: PropTypes.arrayOf(PropTypes.shape({})),
  expandRow: PropTypes.shape({
    expandHeaderColumnRenderer: PropTypes.func,
    expandColumnRenderer: PropTypes.func,
    renderer: PropTypes.func,
  }),
};

export default DataTable;
